// v4 (V4-H2, spec-v4 5.1, 5.3, 5.4): a collection (/k/:id): its one next step, its videos, its cast, and what it
// shares as folds (look and music, locations, its library, settings) with "from the channel" chips beside every
// value. A video whose render-time settings the collection changed since its first finished version shows the
// "The series has new theme music. Use it?" notice here (never folded), and Use it adopts it (POST adopt).
// The cast is components/Cast.tsx's row; a tile opens the Character screen (/k/:id/cast/:characterId, V4-C4).
import { useEffect, useMemo, useState } from "react";
import {
  collectionLine,
  collections,
  collectionsChanged,
  fromChip,
  kindNoun,
  looksOfGroup,
  settingRows,
  stepHref,
  toastError,
  crumbs as makeCrumbs,
  type CollectionDetail,
  type PendingSetting,
  type SettingRow,
  type VideoCard,
} from "../collections";
import { characterPath } from "../characters";
import { CastRow } from "../components/Cast";
import { Crumbs } from "../components/Crumbs";
import { Empty, ErrorNote, Icon, Sheet, Spinner, useLoad } from "../components/bits";
import { Fold } from "../components/Fold";
import { useFormats } from "../components/FormatMark";
import { MoreMenu } from "../components/MoreMenu";
import { NextStep } from "../components/NextStep";
import { Notice } from "../components/Notice";
import { Shell } from "../components/Shell";
import { VideoPoster } from "../components/TreeCards";
import { director } from "../director";
import { foldSummary } from "../fold";
import { ASPECT_WORD } from "../formats";
import type { Aspect } from "../types";
import type { NextStepData } from "../nextstep";
import { Link, navigate } from "../router";
import { openNewVideo, toast, useRefresh, useStore } from "../store";

/** Keys the "Look and music" fold holds; everything else is under Settings. */
const LOOK_KEYS = new Set(["lookId", "aspect", "imageModel", "themeMusicAssetId", "songAssetId", "musicAssetId", "captionStyle", "boardLook", "colors", "logoAssetId", "introStyle"]);
/** Videos whose pending "Use it?" the screen asks for. */
const PENDING_MAX = 12;
const ASPECTS: Aspect[] = ["16:9", "9:16", "1:1"];

export function CollectionScreen({ id, focusCast }: { id: string; focusCast?: string }) {
  const tick = useRefresh("collections", "projects");
  const { data: d, setData, error, loading, reload } = useLoad<CollectionDetail>(() => collections.detail(id), [id, tick]);
  const channels = useStore((s) => s.me?.channels ?? []);
  const formats = useFormats();
  const [archiving, setArchiving] = useState(false);
  const [editing, setEditing] = useState(false);
  const pending = usePending(d?.videos ?? null);

  useEffect(() => {
    if (!d) return;
    director.context(`The user is looking at the collection "${d.collection.name}" (${d.collection.id}, a ${kindNoun(d.collection.kind)} with ${d.videos.length} videos).`);
  }, [d?.collection.id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (focusCast && d) document.getElementById(`cast-${focusCast}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [focusCast, d?.collection.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (error && !d)
    return (
      <Shell>
        <div className="page">
          {error.toLowerCase().includes("not found") ? (
            <Empty title="This collection isn't here" action={<Link to="/" className="btn btn-primary">Go home</Link>}>
              It may have been archived, or the link belongs to another account.
            </Empty>
          ) : (
            <ErrorNote text={`Couldn't open this collection. ${error}`} retry={reload} />
          )}
        </div>
      </Shell>
    );
  if (!d)
    return (
      <Shell>
        <div className="page">{loading && <Spinner label="Opening the collection" />}</div>
      </Shell>
    );

  const c = d.collection;
  const channel = channels.find((x) => x.id === c.channelId) ?? null;
  const looks = looksOfGroup(c.group, formats);
  const rows = settingRows(d.settings, c.kind, { looks });
  const lookRows = rows.filter((r) => LOOK_KEYS.has(r.key));
  const otherRows = rows.filter((r) => !LOOK_KEYS.has(r.key));
  const noun = kindNoun(c.kind);
  const places = [...d.locations.locations, ...d.locations.props];
  const narrator = rows.find((r) => r.key === "narrator");
  const startFormat = d.videos.at(-1)?.format ?? c.formats[0];

  const patchSettings = async (patch: Record<string, unknown>, said: string) => {
    try {
      const next = await collections.patch(c.id, { settings: patch });
      setData({ ...d, collection: { ...c, settings: next.settings } });
      collectionsChanged();
      director.context(`The user ${said} on the collection "${c.name}" by tapping.`);
    } catch (e) {
      toastError("Couldn't change that.", e);
    }
  };
  const onNext = (s: NextStepData) => {
    const to = stepHref(s);
    if (!to) return;
    director.context(`The user tapped the next step "${s.label}" on the collection "${c.name}".`);
    navigate(to);
  };

  return (
    <Shell>
      <div className="page kpage">
        <Crumbs items={makeCrumbs({ channel, collection: c })} />
        <header className="kpage-head">
          <div className="kpage-title">
            <h1 className="page-title">{c.name}</h1>
            <p className="kpage-line">{collectionLine(c, d.settings, looks)}</p>
            {c.description && <p className="page-lede">{c.description}</p>}
          </div>
          <MoreMenu
            label={`More for ${c.name}`}
            items={[
              { id: "rename", label: "Rename", icon: "edit", onSelect: () => setEditing(true), dataAction: "collection.rename" },
              { id: "archive", label: `Archive the ${noun}`, hint: "Its videos become one-offs", icon: "trash", onSelect: () => setArchiving(true), dataAction: "collection.archive" },
            ]}
          />
        </header>

        <NextStep step={d.nextStep} onAct={onNext} />

        {pending.map(({ video, item }) => (
          <Notice
            key={`${video.id}-${item.key}`}
            tone="info"
            code="SETTINGS_CHANGED"
            text={`${video.title}: ${item.text}`}
            action={<AdoptButton projectId={video.id} item={item} title={video.title} />}
          />
        ))}

        <section className="home-section" aria-labelledby="kvids-h">
          <header className="section-head">
            <h2 id="kvids-h">
              Videos <span className="section-n">{d.videos.length}</span>
            </h2>
            <button data-action="collection.new-video" type="button" className="btn btn-quiet btn-small" onClick={() => openNewVideo({ format: startFormat, channelId: c.channelId, collectionId: c.id, formats: c.formats })}>
              <Icon name="plus" size={16} /> {c.kind === "series" || c.kind === "show" ? "New episode" : "New video"}
            </button>
          </header>
          {d.videos.length ? (
            <ul className="poster-row kpage-videos">
              {d.videos.map((v) => (
                <li key={v.id}>
                  <VideoPoster v={v} older={v.olderLooks} />
                </li>
              ))}
            </ul>
          ) : (
            <p className="muted home-none">No videos yet. The first one you make here uses everything the {noun} shares.</p>
          )}
        </section>

        {(d.characters.length > 0 || c.group === "story" || c.group === "song") && (
          <section className="home-section" aria-labelledby="kcast-h">
            <header className="section-head">
              <h2 id="kcast-h">
                Cast <span className="section-n">{d.characters.length}</span>
              </h2>
            </header>
            {d.characters.length ? (
              <CastRow members={d.characters} hrefOf={(m) => characterPath({ collectionId: c.id }, m.id)} focus={focusCast} />
            ) : (
              <p className="muted home-none">Characters made in this {noun}'s videos join its cast, so every episode draws them the same way.</p>
            )}
          </section>
        )}

        <div className="kpage-folds">
          <Fold id="collection.look" title="Look and music" summary={foldSummary(lookRows.slice(0, 3).map((r) => r.text))}>
            <div className="krows">
              <label className="krow krow-edit">
                <span className="krow-label">Look</span>
                <span className="select">
                  <select data-action="collection.look" value={String(c.settings.lookId ?? "")} onChange={(e) => void patchSettings({ lookId: e.target.value || null }, e.target.value ? `set the look to ${looks.find((l) => l.id === e.target.value)?.name ?? e.target.value}` : "cleared the look")}>
                    <option value="">{d.settings.lookId && d.settings.lookId.from !== "collection" ? `${fromChip(d.settings.lookId.from, c.kind)}: ${lookRows.find((r) => r.key === "lookId")?.text}` : "Each video's own"}</option>
                    {looks.map((l) => (
                      <option key={l.id} value={l.id}>
                        {l.name}
                      </option>
                    ))}
                  </select>
                </span>
              </label>
              <label className="krow krow-edit">
                <span className="krow-label">Shape</span>
                <span className="select">
                  <select data-action="collection.look" value={String(c.settings.aspect ?? "")} onChange={(e) => void patchSettings({ aspect: e.target.value || null }, e.target.value ? `set the shape to ${e.target.value}` : "cleared the shape")}>
                    <option value="">Each video's own</option>
                    {ASPECTS.map((a) => (
                      <option key={a} value={a}>
                        {ASPECT_WORD[a]} {a}
                      </option>
                    ))}
                  </select>
                </span>
              </label>
              {lookRows
                .filter((r) => r.key !== "lookId" && r.key !== "aspect")
                .map((r) => (
                  <Row key={r.key} r={r} />
                ))}
            </div>
            <p className="field-hint">A change reaches shots made after it. Finished videos stay as they are.</p>
          </Fold>

          {c.group === "story" && (
            <Fold id="collection.places" title="Locations" count={places.length} summary={places.map((p) => p.name).join(", ") || "None yet"}>
              {places.length ? (
                <ul className="kplaces">
                  {places.map((p) => (
                    <li key={p.id}>
                      <strong>{p.name}</strong>
                      {p.description && <span className="muted"> {p.description}</span>}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="muted">Name the places the story keeps coming back to (“Gran's kitchen”) and every scene there looks the same. Ask your Director to add one.</p>
              )}
            </Fold>
          )}

          <Fold id="collection.library" title={`${noun[0]!.toUpperCase()}${noun.slice(1)} library`} summary="Pictures, music and clips for these videos">
            <Link to={`/library?scope=${encodeURIComponent(`collection:${c.id}`)}`} className="btn btn-quiet btn-small">
              Open the library
            </Link>
          </Fold>

          <Fold id="collection.settings" title="Settings" summary={narrator ? `${narrator.text} narrates` : foldSummary(otherRows.slice(0, 2).map((r) => r.text))}>
            <div className="krows">
              {otherRows.map((r) => (
                <Row key={r.key} r={r} />
              ))}
              {!otherRows.length && <p className="muted">Nothing else set. Videos use the channel's narrator and their own choices.</p>}
            </div>
            <div className="kpage-acts">
              <button type="button" className="btn btn-quiet btn-small" onClick={() => setEditing(true)}>
                <Icon name="edit" size={15} /> Rename
              </button>
              <button type="button" className="btn btn-quiet btn-small" onClick={() => setArchiving(true)}>
                Archive the {noun}
              </button>
            </div>
          </Fold>
        </div>
      </div>

      {editing && <RenameSheet d={d} onClose={() => setEditing(false)} onSaved={(name, description) => setData({ ...d, collection: { ...c, name, description } })} />}
      {archiving && (
        <Sheet title={`Archive ${c.name}?`} onClose={() => setArchiving(false)}>
          <div className="sheet-body">
            <p>
              Its {d.videos.length === 1 ? "video becomes a one-off" : `${d.videos.length} videos become one-offs`} on {channel?.name ?? "your channel"}. Nothing is deleted.
            </p>
            <div className="sheet-actions">
              <button type="button" className="btn btn-quiet" onClick={() => setArchiving(false)}>
                Keep it
              </button>
              <button
                type="button"
                className="btn btn-primary"
                data-autofocus
                onClick={async () => {
                  try {
                    await collections.archive(c.id);
                    collectionsChanged();
                    toast(`${c.name} is archived. Its videos are one-offs now.`, "good");
                    director.context(`The user archived the collection "${c.name}" by tapping; its videos are one-offs now.`);
                    navigate(`/c/${encodeURIComponent(c.channelId)}`);
                  } catch (e) {
                    toastError("Couldn't archive it.", e);
                  }
                }}
              >
                Archive
              </button>
            </div>
          </div>
        </Sheet>
      )}
    </Shell>
  );
}

function Row({ r }: { r: SettingRow }) {
  return (
    <div className="krow">
      <span className="krow-label">{r.label}</span>
      <span className="krow-value">{r.text}</span>
      <span className={`from-chip from-${r.from}`}>{r.chip}</span>
    </div>
  );
}

/** Each video's pending "Use it?" (GET /projects/:id/settings), for the first PENDING_MAX videos. */
function usePending(videos: VideoCard[] | null) {
  const tick = useRefresh("collections", "projects");
  const [out, setOut] = useState<{ video: VideoCard; item: PendingSetting }[]>([]);
  const key = videos?.map((v) => v.id).join(",") ?? "";
  useEffect(() => {
    if (!videos) return;
    let live = true;
    void Promise.all(videos.slice(0, PENDING_MAX).map((v) => collections.videoSettings(v.id).then((s) => s.pending.map((item) => ({ video: v, item }))).catch(() => []))).then((all) => live && setOut(all.flat()));
    return () => {
      live = false;
    };
  }, [key, tick]); // eslint-disable-line react-hooks/exhaustive-deps
  return useMemo(() => out, [out]);
}

export function AdoptButton({ projectId, item, title }: { projectId: string; item: PendingSetting; title: string }) {
  const [busy, setBusy] = useState(false);
  return (
    <button
      data-action="collection.use-update"
      type="button"
      className="btn btn-quiet btn-small"
      disabled={busy}
      onClick={async () => {
        setBusy(true);
        try {
          await collections.adopt(projectId, [item.key]);
          collectionsChanged();
          toast("Done. The next version uses it.", "good");
          director.context(`The user took the collection's new ${item.key} into "${title}" (${projectId}) by tapping Use it. The next render uses it.`);
        } catch (e) {
          toastError("Couldn't use it.", e);
          setBusy(false);
        }
      }}
    >
      Use it
    </button>
  );
}

function RenameSheet({ d, onClose, onSaved }: { d: CollectionDetail; onClose: () => void; onSaved: (name: string, description: string) => void }) {
  const [name, setName] = useState(d.collection.name);
  const [description, setDescription] = useState(d.collection.description);
  const [busy, setBusy] = useState(false);
  return (
    <Sheet title={`Rename the ${kindNoun(d.collection.kind)}`} onClose={onClose}>
      <form
        className="sheet-body"
        onSubmit={async (e) => {
          e.preventDefault();
          if (!name.trim()) return;
          setBusy(true);
          try {
            await collections.patch(d.collection.id, { name: name.trim(), description: description.trim() });
            onSaved(name.trim(), description.trim());
            collectionsChanged();
            director.context(`The user renamed the collection "${d.collection.name}" to "${name.trim()}" by tapping.`);
            onClose();
          } catch (err) {
            toastError("Couldn't rename it.", err);
            setBusy(false);
          }
        }}
      >
        <label className="field">
          <span className="field-label">Name</span>
          <input value={name} onChange={(e) => setName(e.target.value)} maxLength={120} data-autofocus />
        </label>
        <label className="field">
          <span className="field-label">What it's about (optional)</span>
          <textarea value={description} onChange={(e) => setDescription(e.target.value)} maxLength={600} rows={3} />
        </label>
        <button data-action="collection.rename" type="submit" className="btn btn-primary btn-block" disabled={busy || !name.trim()}>
          {busy ? "Saving" : "Save"}
        </button>
      </form>
    </Sheet>
  );
}
